import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useIndexDb } from "./useIndexDb";
import { useChats } from "./useChats";
import { patientForRecording } from "../utils/recordingPatientName";
import { canPickSaveDirectory, pickSaveDirectory, writeAudioToDirectory } from "../utils/saveAudioFile";

// "Save every one of these recordings to the device", shared by the Local
// Recordings manager and the sign-in backlog alert.
//
// One implementation on purpose: both offer it immediately before a bulk DELETE,
// so the two must agree exactly on what counts as saved. A second copy that
// reported a partial run as a success would be a way to delete audio somebody
// believes they still have.
//
// For the same reason "saved" here has to be TRUE, not hopeful. The first version
// counted a recording as saved when its anchor download's `a.click()` did not
// throw — but N script-initiated downloads after N awaits is exactly what Chrome's
// "multiple automatic downloads" block and Safari's user-gesture rule drop, and
// they drop them silently. So:
//   - Where the browser can ask for a folder (File System Access API, Chromium),
//     every file is written through a handle and each write's result is known.
//     Those toasts are the truth.
//   - Where it cannot (Firefox, Safari, the extension iframe), the anchor path is
//     all there is, and the completion message says what actually happened: the
//     files were HANDED to the browser — go and check before deleting anything.
//     It is never the "Saved N" success toast.
export function useBulkSaveRecordings() {
  const { t } = useTranslation();
  // The progress flag lives in the provider so the manager and the backlog alert
  // see ONE run: each used to keep its own, so a save started from the alert left
  // the manager's controls live (and vice versa).
  const { exportRecording, bulkSave, setBulkSave } = useIndexDb();
  const { chats } = useChats();

  // Sequential on purpose: each export stitches a whole recording's segments into
  // one MP3, so running them in parallel would hold N assembled recordings in
  // memory at once — and this exists for the queue that is already hundreds of
  // rows deep. It also spaces the anchor downloads out.
  //
  // Resolves { saved, failed, verified } — `verified` is whether the counts were
  // confirmed by directory writes (true) or are anchor hand-offs (false) — or
  // { saved: 0, failed: 0, cancelled: true } when the folder picker was closed.
  const saveAll = async (rows) => {
    if (bulkSave || !rows || rows.length === 0) return { saved: 0, failed: 0 };

    // FIRST, before any other await: the folder picker only opens under the click's
    // own user activation, and a single await ahead of it is enough to lose that.
    let handle = null;
    if (canPickSaveDirectory()) {
      const picked = await pickSaveDirectory();
      // Closing the picker is a decision, not a failure — nothing to report.
      if (picked.cancelled) return { saved: 0, failed: 0, cancelled: true };
      handle = picked.handle;
    }
    const sink = handle ? (blob, fileName) => writeAudioToDirectory(handle, blob, fileName) : null;

    let saved = 0;
    let failed = 0;
    setBulkSave({ done: 0, total: rows.length });
    try {
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        try {
          const options = { patient: patientForRecording(chats, r).name };
          if (sink) options.sink = sink;
          const ok = await exportRecording(r.keys, options);
          if (ok) saved++;
          else failed++;
        } catch (e) {
          // One unreadable recording must not strand the rest — this is the last
          // chance to get any of them off the device.
          console.error("Could not export a queued recording during Save all", e?.name || e);
          failed++;
        }
        setBulkSave({ done: i + 1, total: rows.length });
      }
    } finally {
      setBulkSave(null);
    }
    // A partial result is reported AS a failure. "Saved 9 of 12" announced as
    // success is how somebody deletes the other three believing they have them.
    // And "Saved" is only ever said of a verified write: on the anchor path the
    // ones that did not fail were merely HANDED to the browser, which may have
    // dropped all but the first — so the partial there says that, and names the
    // failures, rather than counting hand-offs as saves.
    if (handle) {
      if (failed > 0) toast.error(t("layout.recordingsManager.saveAllPartial", { saved, failed }));
      else if (saved > 0) toast.success(t("layout.recordingsManager.saveAllDone", { count: saved }));
    } else if (failed > 0) {
      toast.error(t("layout.recordingsManager.saveAllHandedToBrowserPartial", { count: saved, failed }));
    } else if (saved > 0) {
      toast.warning(t("layout.recordingsManager.saveAllHandedToBrowser", { count: saved }));
    }
    return { saved, failed, verified: !!handle };
  };

  const saveAllLabel = (count) =>
    bulkSave
      ? t("layout.recordingsManager.savingProgress", { done: bulkSave.done, total: bulkSave.total })
      : t("layout.recordingsManager.saveAll", { count });

  return { bulkSave, saveAll, saveAllLabel };
}
